import { useState, useCallback } from "react";
import { siteData, StatusTagGroup } from "@/store/siteData";

const PRESET_COLORS = [
  "#10B981", "#F59E0B", "#94A3B8", "#F43F5E", "#3B82F6",
  "#8B5CF6", "#06B6D4", "#F97316", "#84CC16", "#EC4899",
];

function ColorPicker({ value, onChange }: { value: string; onChange: (c: string) => void }) {
  const [showCustom, setShowCustom] = useState(false);
  return (
    <div className="flex flex-wrap items-center gap-2">
      {PRESET_COLORS.map((c) => (
        <button
          key={c}
          onClick={() => onChange(c)}
          className="w-7 h-7 rounded-full border-2 cursor-pointer transition-transform hover:scale-110 flex-shrink-0"
          style={{ background: c, borderColor: value === c ? "#fff" : "transparent" }}
          title={c}
        />
      ))}
      <button
        onClick={() => setShowCustom((p) => !p)}
        className="w-7 h-7 rounded-full border-2 border-dashed border-[#3B82F6] flex items-center justify-center cursor-pointer hover:border-white transition-colors flex-shrink-0"
      >
        <i className="ri-palette-line text-[#7B8FAF] text-xs"></i>
      </button>
      {showCustom && (
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-7 h-7 rounded cursor-pointer border-0 bg-transparent"
        />
      )}
    </div>
  );
}

export default function StatusTagsEditor() {
  const [tags, setTags] = useState<StatusTagGroup[]>(() => siteData.getStatusTags());
  const [editingId, setEditingId] = useState<string | null>(null);
  const [newLabel, setNewLabel] = useState("");
  const [newColor, setNewColor] = useState("#10B981");
  const [saved, setSaved] = useState(false);

  const handleSave = async () => {
    await siteData.saveStatusTags(tags);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const updateTag = useCallback((id: string, key: keyof StatusTagGroup, val: string) => {
    setTags((prev) => prev.map((t) => (t.id === id ? { ...t, [key]: val } : t)));
  }, []);

  const addTag = () => {
    if (!newLabel.trim()) return;
    const slug = newLabel.toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "");
    const id = `${slug}_${Date.now()}`;
    setTags((prev) => [...prev, { id, label: newLabel.trim(), color: newColor }]);
    setNewLabel("");
    setNewColor("#10B981");
  };

  const removeTag = (id: string) => {
    if (tags.length <= 1) return;
    setTags((prev) => prev.filter((t) => t.id !== id));
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between pb-4 border-b border-[#2B3654]">
        <div>
          <h2 className="text-white font-semibold text-base">Proje Durum Etiketleri</h2>
          <p className="text-[#7B8FAF] text-xs mt-0.5">
            Projelerde kullan\u0131lan durum etiketlerini y\u00f6netin. Renk ve isim d\u00fczenleme, yeni etiket ekleme.
          </p>
        </div>
        <button
          onClick={handleSave}
          className={`px-4 py-2 text-sm font-medium rounded-lg cursor-pointer whitespace-nowrap transition-all flex items-center gap-2 flex-shrink-0 ${
            saved ? "bg-emerald-500 text-white" : "bg-[#3B82F6] hover:bg-[#3d4e6a] text-white"
          }`}
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className={saved ? "ri-check-line" : "ri-save-line"}></i>
          </div>
          {saved ? "Kaydedildi!" : "Kaydet"}
        </button>
      </div>

      {/* Mevcut etiketler */}
      <div className="space-y-3">
        {tags.map((tag) => (
          <div key={tag.id} className="bg-[#0F1729] border border-[#2B3654] rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <span
                  className="px-3 py-1 rounded-full text-white text-xs font-semibold whitespace-nowrap flex-shrink-0"
                  style={{ background: tag.color }}
                >
                  {tag.label}
                </span>
                <span className="text-[#465886] text-xs font-mono truncate">id: {tag.id}</span>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => setEditingId(editingId === tag.id ? null : tag.id)}
                  className="w-7 h-7 flex items-center justify-center text-[#7B8FAF] hover:text-white cursor-pointer rounded-lg hover:bg-white/5 transition-colors"
                  title="D\u00fczezenle"
                >
                  <i className="ri-pencil-line text-sm"></i>
                </button>
                {tags.length > 1 && (
                  <button
                    onClick={() => removeTag(tag.id)}
                    className="w-7 h-7 flex items-center justify-center text-[#7B8FAF] hover:text-red-400 cursor-pointer rounded-lg hover:bg-red-500/10 transition-colors"
                    title="Sil"
                  >
                    <i className="ri-delete-bin-line text-sm"></i>
                  </button>
                )}
              </div>
            </div>

            {editingId === tag.id && (
              <div className="pt-3 border-t border-[#2B3654] space-y-3">
                <div>
                  <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-1.5">
                    Etiket Ad\u0131
                  </label>
                  <input
                    type="text"
                    value={tag.label}
                    onChange={(e) => updateTag(tag.id, "label", e.target.value)}
                    className="w-full bg-[#141E35] border border-[#2B3654] text-white text-sm rounded-lg px-3 py-2 outline-none focus:border-[#3B82F6]"
                  />
                </div>
                <div>
                  <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-2">Renk</label>
                  <ColorPicker value={tag.color} onChange={(v) => updateTag(tag.id, "color", v)} />
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Yeni etiket ekleme */}
      <div className="bg-[#0F1729] border border-dashed border-[#2B3654] hover:border-[#3B82F6] rounded-xl p-4 space-y-3 transition-colors">
        <p className="text-[#7B8FAF] text-xs font-semibold uppercase tracking-widest">
          Yeni Etiket Grubu Ekle
        </p>
        <div className="flex gap-3 items-end">
          <div className="flex-1">
            <label className="block text-[#7B8FAF] text-xs mb-1.5">Etiket Ad\u0131</label>
            <input
              type="text"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addTag()}
              placeholder="\u00f6rn: \u0130ptal Edildi"
              className="w-full bg-[#141E35] border border-[#2B3654] text-white text-sm rounded-lg px-3 py-2 outline-none focus:border-[#3B82F6]"
            />
          </div>
          <button
            onClick={addTag}
            className="px-4 py-2 bg-[#3B82F6]/20 text-[#B8C5D8] border border-[#3B82F6]/40 rounded-lg text-sm cursor-pointer whitespace-nowrap hover:bg-[#3B82F6]/40 transition-colors"
          >
            <i className="ri-add-line mr-1"></i> Ekle
          </button>
        </div>
        <div>
          <label className="block text-[#7B8FAF] text-xs mb-2">Renk</label>
          <ColorPicker value={newColor} onChange={setNewColor} />
        </div>
        {newLabel && (
          <div className="flex items-center gap-2 pt-1">
            <span className="text-[#7B8FAF] text-xs">\u00d6nizleme:</span>
            <span
              className="px-3 py-1 rounded-full text-white text-xs font-semibold"
              style={{ background: newColor }}
            >
              {newLabel}
            </span>
          </div>
        )}
      </div>

      {/* Kullan\u0131m bilgisi */}
      <div className="bg-[#141E35] border border-[#2B3654] rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-5 h-5 flex items-center justify-center text-[#3B82F6]">
            <i className="ri-information-line text-base"></i>
          </div>
          <p className="text-white text-sm font-semibold">Kullan\u0131m</p>
        </div>
        <p className="text-[#7B8FAF] text-xs leading-relaxed">
          Bu etiketler proje kart\u0131nda durum g\u00f6stergesi olarak kullan\u0131l\u0131r.
          Projeler edit\u00f6r\u00fcnde her proje i\u00e7in bu listeden bir durum se\u00e7ebilirsiniz.
          Renk ve ad de\u011fi\u015fikli\u011fi, tüm projeleri otomatik g\u00fcnceller.
        </p>
        <div className="flex flex-wrap gap-2 mt-3">
          {tags.map((t) => (
            <span
              key={t.id}
              className="px-2.5 py-1 rounded-full text-white text-xs font-medium"
              style={{ background: t.color }}
            >
              {t.label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
